// Language detection utilities
import {
    extractMainDomain,
    getCTAUrl,
    getDefaultLanguage,
    getLanguageSpecificDemoURLs,
    isAliasDomain,
} from '../../config/SiteConfigManager';
import { logger } from '../core/logger';
import {
    getBaseLanguage,
    getLocaleLookupCandidates,
    isLocaleCode,
    normalizeLocaleCode,
} from './localeUtils';
//
// Usage Examples:
//
// 1. Detect user's language (from URL first, then browser):
//    const userLang = detectUserLanguage(); // Auto-detects from current URL or browser
//    const userLangFromURL = detectUserLanguage('https://www.abtasty.com/fr/get-a-demo/'); // Returns 'fr'
//
// 2. Detect language from URL only:
//    const urlLang = detectLanguageFromURL('https://www.abtasty.com/de/demo-anfordern/'); // Returns 'de'
//    const subdomainLang = detectLanguageFromURL('https://fr.abtasty.com/demo/'); // Returns 'fr'
//
// 3. Get demo URL for user's language:
//    const demoURL = getDemoURLForLanguage('abtasty.com'); // Auto-detects language
//    const frenchDemoURL = getDemoURLForLanguage('abtasty.com', 'fr'); // Force French
//
// 4. Get all available demo URLs for a site:
//    const allURLs = getAllDemoURLs('abtasty.com'); // Returns object with all language URLs
//
// 5. Check if a language is supported:
//    const isSupported = isLanguageSupported('fr'); // Returns true
//    const isNotSupported = isLanguageSupported('zh'); // Returns false

/**
 * Supported languages for demo URLs
 */
export type SupportedLanguage = 'en' | 'fr' | 'de' | 'es' | 'it' | 'pt' | 'bg' | 'nl' | 'ro' | 'hu';

export type SupportedLocale = SupportedLanguage | `${SupportedLanguage}-${string}`;
export type SupportedConversationLocale = 'en-gb' | 'en-us';
export type SupportedConversationLanguage =
    | 'auto'
    | SupportedLanguage
    | SupportedConversationLocale;

/**
 * Default language fallback
 */
export const DEFAULT_LANGUAGE: SupportedLanguage = 'en';

/**
 * Supported languages list
 */
export const SUPPORTED_LANGUAGES: SupportedLanguage[] = [
    'en',
    'fr',
    'de',
    'es',
    'it',
    'pt',
    'bg',
    'nl',
    'ro',
    'hu',
];

const SUPPORTED_CONVERSATION_LOCALES: SupportedConversationLocale[] = ['en-gb', 'en-us'];

/**
 * Language name mappings for display purposes
 */
export const LANGUAGE_NAMES: Record<SupportedLanguage, string> = {
    en: 'English',
    fr: 'Français',
    de: 'Deutsch',
    es: 'Español',
    it: 'Italiano',
    pt: 'Português',
    bg: 'Български',
    nl: 'Nederlands',
    ro: 'Română',
    hu: 'Magyar',
};

const toSupportedLanguage = (value?: string | null): SupportedLanguage | null => {
    const baseLanguage = getBaseLanguage(value);

    if (baseLanguage && SUPPORTED_LANGUAGES.includes(baseLanguage as SupportedLanguage)) {
        return baseLanguage as SupportedLanguage;
    }

    return null;
};

const toSupportedConversationLocale = (
    value?: string | null
): SupportedConversationLocale | null => {
    const normalized = normalizeLocaleCode(value);

    if (
        normalized &&
        SUPPORTED_CONVERSATION_LOCALES.includes(normalized as SupportedConversationLocale)
    ) {
        return normalized as SupportedConversationLocale;
    }

    return null;
};

/**
 * Normalize a locale-like value for the chat API's conversationLanguage field.
 *
 * URL/content locale detection intentionally keeps regional variants such as
 * fr-fr so locale-aware maps can resolve exact keys first. The conversation
 * API only accepts base languages, except for the explicitly modeled English
 * regional variants used for content routing.
 */
export const normalizeConversationLanguage = (
    value?: string | null
): SupportedConversationLanguage | null => {
    const normalized = normalizeLocaleCode(value);
    if (!normalized) {
        return null;
    }

    if (normalized === 'auto') {
        return 'auto';
    }

    const conversationLocale = toSupportedConversationLocale(normalized);
    if (conversationLocale) {
        return conversationLocale;
    }

    return toSupportedLanguage(normalized);
};

/**
 * Detect a locale from the URL path or subdomain.
 * Returns the explicit locale when present (for example en-gb), otherwise null.
 */
export const detectLocaleFromURL = (url: string): string | null => {
    try {
        const urlObj = new URL(url);
        const pathname = urlObj.pathname;

        const pathSegments = pathname.split('/').filter((segment) => segment.length > 0);

        if (pathSegments.length > 0) {
            const firstSegment = normalizeLocaleCode(pathSegments[0]);

            if (firstSegment && isLocaleCode(firstSegment) && toSupportedLanguage(firstSegment)) {
                return firstSegment;
            }
        }

        const hostname = urlObj.hostname;
        const hostParts = hostname.split('.');

        if (hostParts.length > 2) {
            const subdomain = normalizeLocaleCode(hostParts[0]);

            if (subdomain && isLocaleCode(subdomain) && toSupportedLanguage(subdomain)) {
                return subdomain;
            }
        }

        return null;
    } catch (error) {
        logger.warn('Error detecting locale from URL:', error);
        return null;
    }
};

/**
 * Detect a bare country path segment such as /be/ or /ch/ (IX-5600). It names a
 * region, not a language: `be` would otherwise read as Belarusian. Supported
 * language codes are handled by `detectLocaleFromURL` first.
 */
const detectRegionFromURL = (url: string): string | null => {
    try {
        const [firstSegment] = new URL(url).pathname.split('/').filter(Boolean);
        const region = normalizeLocaleCode(firstSegment);
        if (!region || !/^[a-z]{2}$/.test(region) || toSupportedLanguage(region)) return null;

        const regionNames = new Intl.DisplayNames(['en'], { type: 'region', fallback: 'none' });
        return regionNames.of(region.toUpperCase()) ? region : null;
    } catch {
        return null;
    }
};

/**
 * Detect language from URL path
 * @param url - The URL to analyze
 * @returns The detected language code or null if not found
 */
export const detectLanguageFromURL = (url: string): SupportedLanguage | null => {
    return toSupportedLanguage(detectLocaleFromURL(url));
};

/**
 * Detect the user's locale based on URL and browser settings.
 * Unlike browser locale, regional variants are only kept when explicitly present in the URL.
 * Generic pages still resolve to base languages such as "en".
 */
export const detectUserLocale = (url?: string, domain?: string): string => {
    try {
        const targetUrl = url || window.location.href;
        const urlLocale = detectLocaleFromURL(targetUrl);
        if (urlLocale) {
            return urlLocale;
        }

        // A country segment (/be/) adds its region to the detected language, so
        // regional CTA keys such as fr-be resolve; fr-be falls back to fr when
        // the config has no regional key. The page's <html lang="fr-BE"> names
        // the language when it agrees with that country.
        const region = detectRegionFromURL(targetUrl);
        if (region) {
            const isLiveDocument =
                typeof document !== 'undefined' && (!url || url === window.location.href);
            const htmlLocale = isLiveDocument
                ? normalizeLocaleCode(document.documentElement?.lang)
                : null;
            if (htmlLocale?.endsWith(`-${region}`) && toSupportedLanguage(htmlLocale)) {
                return htmlLocale;
            }
            return `${detectLanguageWithoutURL(targetUrl, url, domain)}-${region}`;
        }

        return detectLanguageWithoutURL(targetUrl, url, domain);
    } catch (error) {
        logger.warn('Error detecting user locale:', error);
        return DEFAULT_LANGUAGE;
    }
};

/** Language from the alias page's <html lang>, the site default, then the browser. */
const detectLanguageWithoutURL = (
    targetUrl: string,
    url?: string,
    domain?: string
): SupportedLanguage => {
    // Resolve the registrable domain once — used both to gate the <html lang>
    // tier on alias-ness and for the config default_language lookup below.
    let mainDomain: string | undefined;
    try {
        // extractMainDomain handles multi-part TLDs (co.uk, com.au); a plain
        // slice(-2) would turn www.eskimoz.co.uk into co.uk and break the
        // alias lookup below.
        mainDomain = domain || extractMainDomain(new URL(targetUrl).hostname);
    } catch {
        mainDomain = domain;
    }

    // The page's own declared language (<html lang="it">) is authoritative
    // ONLY for alias country-TLD sites (e.g. eskimoz.it / eskimoz.co.uk) whose
    // config is borrowed from a different-language canonical (eskimoz.fr) and
    // which signal their real locale solely via <html lang>. For own-config
    // sites the attribute is often stale CMS boilerplate — Webflow ships
    // <html lang="en"> on French sites — so it must NOT override the configured
    // default_language (IX-3729: moka.care). Only trust it when targeting the
    // live document (no url override).
    if (
        mainDomain &&
        isAliasDomain(mainDomain) &&
        typeof document !== 'undefined' &&
        (!url || url === window.location.href)
    ) {
        const htmlLocale = toSupportedLanguage(document.documentElement?.lang);
        if (htmlLocale) {
            return htmlLocale;
        }
    }

    try {
        if (mainDomain) {
            const defaultLanguage = getDefaultLanguage(mainDomain);
            const supportedDefaultLanguage = toSupportedLanguage(defaultLanguage);

            if (supportedDefaultLanguage) {
                return supportedDefaultLanguage;
            }
        }
    } catch (siteError) {
        logger.warn('Error getting site default language:', siteError);
    }

    const browserLanguage = navigator.language || navigator.languages?.[0];
    const supportedBrowserLanguage = toSupportedLanguage(browserLanguage);

    if (supportedBrowserLanguage) {
        return supportedBrowserLanguage;
    }

    return DEFAULT_LANGUAGE;
};

/**
 * Detect user's preferred language based on URL and browser settings
 * @param url - Optional URL to extract language from, defaults to current page URL
 * @param domain - Optional domain to use for site config lookup (useful for preprod environments)
 * @returns The detected language code or default language if not supported
 */
export const detectUserLanguage = (url?: string, domain?: string): SupportedLanguage => {
    return toSupportedLanguage(detectUserLocale(url, domain)) ?? DEFAULT_LANGUAGE;
};

/**
 * Get the appropriate demo URL for a domain based on user's language
 * @param domain - The domain to get demo URL for
 * @param language - Optional language override, if not provided will auto-detect
 * @returns The demo URL for the specified language or default URL
 */
export const getDemoURLForLanguage = (
    domain: string,
    language?: SupportedLanguage
): string | undefined => {
    const targetLanguage = language || detectUserLanguage(undefined, domain);
    return getCTAUrl(domain, '<<DEMO-BUTTON>>', targetLanguage);
};

/**
 * Check if a language is supported
 * @param language - Language code to check
 * @returns True if language is supported
 */
export const isLanguageSupported = (language: string): language is SupportedLanguage => {
    return SUPPORTED_LANGUAGES.includes(language as SupportedLanguage);
};

/**
 * A locale the widget can detect: a locale code whose base language is supported.
 * Detection always resolves to a supported base, so a key like `be` (a country,
 * read as Belarusian) or `ch` is never looked up (IX-5600).
 */
export const isWidgetLocale = (value?: string | null): boolean =>
    isLocaleCode(value) && toSupportedLanguage(value) !== null;

/**
 * Get all available demo URLs for a domain
 * @param domain - The domain to get demo URLs for
 * @returns Object with language codes as keys and demo URLs as values
 */
export const getAllDemoURLs = (domain: string): Record<string, string> => {
    const demoURLs = getLanguageSpecificDemoURLs(domain);

    if (demoURLs) {
        return demoURLs;
    }

    // If no language-specific URLs, check for default URL
    const defaultURL = getCTAUrl(domain, '<<DEMO-BUTTON>>', 'en');
    if (defaultURL) {
        return SUPPORTED_LANGUAGES.reduce(
            (acc, lang) => {
                acc[lang] = defaultURL;
                return acc;
            },
            {} as Record<string, string>
        );
    }

    return {};
};

/**
 * Get the effective user language, preferring conversationLanguage prop over auto-detection
 * This function is designed for components that need to respect language picker selections in preprod
 * while still supporting auto-detection in widget environment
 *
 * @param conversationLanguage - Optional language override (from language picker or parent component)
 * @param url - Optional URL to extract language from, defaults to current page URL
 * @param domain - Optional domain to use for site config lookup (useful for preprod environments)
 * @returns The effective language code to use
 */
export const getUserLanguage = (
    conversationLanguage?: string,
    url?: string,
    domain?: string
): SupportedLanguage => {
    const supportedConversationLanguage = toSupportedLanguage(conversationLanguage);
    if (supportedConversationLanguage) {
        return supportedConversationLanguage;
    }

    // Otherwise fall back to auto-detection
    return detectUserLanguage(url, domain);
};

/**
 * Get the effective user locale, preferring an explicit override over auto-detection.
 * Locale-aware website content can use this to distinguish en-gb from en-us while still
 * falling back to the base language when no explicit regional URL is present.
 */
export const getUserLocale = (
    conversationLanguage?: string,
    url?: string,
    domain?: string
): string => {
    const normalizedConversationLanguage = normalizeLocaleCode(conversationLanguage);
    if (normalizedConversationLanguage && toSupportedLanguage(normalizedConversationLanguage)) {
        return normalizedConversationLanguage;
    }

    return detectUserLocale(url, domain);
};

export { getBaseLanguage, getLocaleLookupCandidates, isLocaleCode, normalizeLocaleCode };
